import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  Select, SelectContent, SelectGroup, SelectItem, SelectLabel, SelectTrigger, SelectValue,
} from './ui/select';
import {
  Plus, Trash, AddressBook, Phone, Envelope
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { CORPS_ETAT_CATALOG, getCorpsEtatLabel } from '../constants/corpsEtat';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

function EntrepriseRow({ entry, testPrefix, rowBg, onDelete }) {
  const corpsLabel = getCorpsEtatLabel(entry.corps_etat);
  const sous = (entry.sous_corps_etat || '').trim();
  return (
    <div className={`flex items-center justify-between ${rowBg} rounded-lg px-3 py-2 text-sm`}
      data-testid={`${testPrefix}-row-${entry.id}`}>
      <div className="flex-1 min-w-0">
        <span className="font-medium text-[#111827]">{entry.nom}</span>
        {entry.representant && <span className="text-[#6B7280] ml-2">({entry.representant})</span>}
        {corpsLabel && (
          <span className="text-xs text-[#9CA3AF] ml-2">
            {corpsLabel}{sous ? ` — ${sous}` : ''}
          </span>
        )}
        {(entry.telephone || entry.email) && (
          <div className="flex gap-3 mt-0.5">
            {entry.telephone && (
              <span className="text-xs text-[#9CA3AF] flex items-center gap-0.5">
                <Phone size={10} /> {entry.telephone}
              </span>
            )}
            {entry.email && (
              <span className="text-xs text-[#9CA3AF] flex items-center gap-0.5">
                <Envelope size={10} /> {entry.email}
              </span>
            )}
          </div>
        )}
      </div>
      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500 hover:bg-red-50 flex-shrink-0"
        onClick={() => onDelete(entry.id)} data-testid={`${testPrefix}-delete-${entry.id}`}>
        <Trash size={14} />
      </Button>
    </div>
  );
}

function AutocompleteSuggestions({ suggestions, suggestionsRef, testPrefix, onSelect }) {
  return (
    <div ref={suggestionsRef}
      className="absolute z-50 w-full mt-1 bg-white border border-[#E5E7EB] rounded-lg shadow-lg max-h-48 overflow-y-auto"
      data-testid={`${testPrefix}-suggestions`}>
      <div className="px-3 py-1.5 text-[10px] font-semibold text-[#9CA3AF] uppercase tracking-wider border-b bg-[#F9FAFB]">
        <AddressBook size={10} className="inline mr-1" weight="fill" /> Depuis l'annuaire
      </div>
      {suggestions.map(s => (
        <button key={s.id} onClick={() => onSelect(s)}
          className="w-full text-left px-3 py-2 hover:bg-[#F3F4F6] transition-colors border-b border-[#F3F4F6] last:border-0"
          data-testid={`${testPrefix}-suggestion-${s.id}`}>
          <span className="text-sm font-medium text-[#111827]">{s.nom}</span>
          {s.corps_etat && <span className="text-xs text-[#9CA3AF] ml-2">{getCorpsEtatLabel(s.corps_etat)}</span>}
          {s.representant && <span className="text-xs text-[#6B7280] block">{s.representant}</span>}
        </button>
      ))}
    </div>
  );
}

export function EntrepriseManagerShared({ chantierId, variant = 'opc' }) {
  const [entreprises, setEntreprises] = useState([]);
  const [nom, setNom] = useState('');
  const [representant, setRepresentant] = useState('');
  const [corpsEtat, setCorpsEtat] = useState('');
  const [sousCorpsEtat, setSousCorpsEtat] = useState('');
  const [email, setEmail] = useState('');
  const [telephone, setTelephone] = useState('');
  const [adding, setAdding] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [showExtraFields, setShowExtraFields] = useState(false);
  const suggestionsRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  const isOPC = variant === 'opc';
  const accentColor = isOPC ? '#0055FF' : '#FF6B00';
  const accentBg = isOPC ? 'bg-[#0055FF] hover:bg-blue-700' : 'bg-[#FF6B00] hover:bg-orange-700';
  const rowBg = isOPC ? 'bg-[#F3F4F6]' : 'bg-[#FFF7ED]';
  const testPrefix = isOPC ? 'ent' : 'csps-ent';

  const fetchEntreprises = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/chantiers/${chantierId}/entreprises`, { withCredentials: true });
      setEntreprises(res.data || []);
    } catch (_err) { setEntreprises([]); }
  }, [chantierId]);

  useEffect(() => { fetchEntreprises(); }, [fetchEntreprises]);

  // Close suggestions on outside click
  useEffect(() => {
    const handleClick = (e) => {
      if (suggestionsRef.current && !suggestionsRef.current.contains(e.target) &&
          inputRef.current && !inputRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [setShowSuggestions]);

  const searchAnnuaire = useCallback(async (query) => {
    if (query.length < 2) { setSuggestions([]); return; }
    try {
      const res = await axios.get(`${API}/annuaire/search`, {
        params: { q: query }, withCredentials: true
      });
      setSuggestions(res.data || []);
      setShowSuggestions((res.data || []).length > 0);
    } catch (_err) { setSuggestions([]); }
  }, [setSuggestions, setShowSuggestions]);

  const handleNomChange = (value) => {
    setNom(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => searchAnnuaire(value), 250);
  };

  const selectSuggestion = (entry) => {
    setNom(entry.nom || '');
    setRepresentant(entry.representant || '');
    setCorpsEtat(entry.corps_etat || '');
    setSousCorpsEtat(entry.sous_corps_etat || '');
    setEmail(entry.email || '');
    setTelephone(entry.telephone || '');
    setShowSuggestions(false);
    if (entry.email || entry.telephone) setShowExtraFields(true);
    toast.success(`${entry.nom} importe depuis l'annuaire`);
  };

  const handleAdd = async () => {
    if (!nom.trim()) return;
    setAdding(true);
    try {
      await axios.post(`${API}/chantiers/${chantierId}/entreprises`, {
        chantier_id: chantierId, nom, representant,
        corps_etat: corpsEtat, sous_corps_etat: sousCorpsEtat,
        email, telephone,
      }, { withCredentials: true });
      setNom(''); setRepresentant(''); setCorpsEtat(''); setSousCorpsEtat('');
      setEmail(''); setTelephone('');
      setShowExtraFields(false);
      toast.success('Entreprise ajoutée');
      fetchEntreprises();
    } catch (_err) { toast.error("Erreur lors de l'ajout"); }
    finally { setAdding(false); }
  };

  const handleDelete = async (entId) => {
    try {
      await axios.delete(`${API}/chantiers/${chantierId}/entreprises/${entId}`, { withCredentials: true });
      toast.success('Entreprise supprimée');
      fetchEntreprises();
    } catch (_err) { toast.error('Erreur lors de la suppression'); }
  };

  return (
    <div className="space-y-3 pt-2" data-testid={`${testPrefix}-manager`}>
      {/* Existing enterprises */}
      {entreprises.length > 0 && (
        <div className="space-y-1.5">
          {entreprises.map(e => (
            <EntrepriseRow key={e.id} entry={e} testPrefix={testPrefix} rowBg={rowBg} onDelete={handleDelete} />
          ))}
        </div>
      )}

      {/* Add form with autocomplete */}
      <div className="space-y-2">
        <div className="flex flex-col sm:flex-row gap-2">
          <div className="relative flex-1" ref={inputRef}>
            <Input value={nom} onChange={e => handleNomChange(e.target.value)}
              onFocus={() => { if (suggestions.length > 0) setShowSuggestions(true); }}
              placeholder="Nom entreprise *"
              className="h-9 text-sm" data-testid={`${testPrefix}-nom-input`} />
            {showSuggestions && suggestions.length > 0 && (
              <AutocompleteSuggestions suggestions={suggestions} suggestionsRef={suggestionsRef}
                testPrefix={testPrefix} onSelect={selectSuggestion} />
            )}
          </div>
          <Input value={representant} onChange={e => setRepresentant(e.target.value)} placeholder="Representant"
            className="h-9 text-sm flex-1" data-testid={`${testPrefix}-representant-input`} />
          <Button onClick={handleAdd} disabled={!nom.trim() || adding} size="sm"
            className={`h-9 ${accentBg} px-4`} data-testid={`${testPrefix}-add-btn`}>
            {adding ? <Loader2 className="h-3 w-3 animate-spin" /> : <Plus size={14} />}
          </Button>
        </div>

        {/* Corps d'état (Select groupé) + sous-corps d'état */}
        <div className="flex flex-col sm:flex-row gap-2">
          <Select value={corpsEtat || '_none'} onValueChange={(v) => setCorpsEtat(v === '_none' ? '' : v)}>
            <SelectTrigger className="h-9 text-sm flex-1" data-testid={`${testPrefix}-corps-select`}>
              <SelectValue placeholder="Corps d'état / Rôle" />
            </SelectTrigger>
            <SelectContent position="popper" className="z-[100] max-h-80">
              <SelectItem value="_none" className="text-[#6B7280]">— Aucun —</SelectItem>
              {CORPS_ETAT_CATALOG.map(g => (
                <SelectGroup key={g.group}>
                  <SelectLabel className="text-[10px] uppercase tracking-widest text-[#0055FF]">{g.group}</SelectLabel>
                  {g.items.map(it => (
                    <SelectItem key={it.value} value={it.value} className="text-sm">
                      {it.label}
                    </SelectItem>
                  ))}
                </SelectGroup>
              ))}
            </SelectContent>
          </Select>
          <Input value={sousCorpsEtat} onChange={e => setSousCorpsEtat(e.target.value)}
            placeholder="Précision / sous-corps d'état (ex: bardage bois, MT/BT…)"
            className="h-9 text-sm flex-1" data-testid={`${testPrefix}-sous-corps-input`} />
        </div>

        {/* Toggle extra fields */}
        {!showExtraFields ? (
          <button onClick={() => setShowExtraFields(true)}
            className="text-xs font-medium flex items-center gap-1 hover:underline"
            style={{ color: accentColor }}
            data-testid={`${testPrefix}-show-extra`}>
            <Plus size={10} /> Email, telephone
          </button>
        ) : (
          <div className="flex flex-col sm:flex-row gap-2">
            <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Email"
              className="h-9 text-sm flex-1" type="email" data-testid={`${testPrefix}-email-input`} />
            <Input value={telephone} onChange={e => setTelephone(e.target.value)} placeholder="Telephone"
              className="h-9 text-sm flex-1" data-testid={`${testPrefix}-tel-input`} />
          </div>
        )}
      </div>
    </div>
  );
}
